import scrollingElement from '../utils/scrollingElement';

// モーダルのトリガー要素を保存する
let triggerElement;
// スクロール位置を保存する
let scrollAmount;

const selector = {
  trigger: '.js-modal-trigger', // モーダルのトリガー要素（CSSセレクター）
  cloneContent: '.js-modal-clone', // モーダルに挿入するクローン対象のコンテンツ（CSSセレクター）
  closeButton: '.js-modal-close', // モーダルを閉じる要素（ID）
  modalID: 'js-modal', // コンテンツを挿入する要素（ID）
  contentID: 'js-modal-content', // コンテンツを挿入する要素（ID）
  focusID: 'js-modal-focus', // モーダルを開くときにフォーカスする要素（ID）
  overlayID: 'js-modal-overlay', // モーダルの背景要素（ID）
};

/**
 * 文字列をローワーキャメルケースに変換
 * @param {string} string 変換する文字列
 * @return {string} 変換された文字列を返す
 * @example toCamelcase('kebab-case') => 'kebabCase'
 */
const toCamelcase = string => {
  if (!string) return string;

  let str = string;
  const strs = str.split(/[-_ ]+/);
  let i = 1;
  const len = strs.length;

  if (len <= 1) return str;

  str = strs[0].toLowerCase();

  for (; i < len; i += 1) {
    str += strs[i].toLowerCase().replace(/^[a-z]/, value => {
      return value.toUpperCase();
    });
  }

  return str;
};

function fixed() {
  const target = scrollingElement();
  target.style.position = 'absolute';
  target.style.top = '0';
  target.style.left = '0';
  target.style.overflow = 'hidden';
  target.style.width = '100vw';
  target.style.height = '100vh';
}

function clearFixed() {
  const target = scrollingElement();
  target.style.position = '';
  target.style.top = '';
  target.style.left = '';
  target.style.overflow = '';
  target.style.width = '';
  target.style.height = '';
}

/**
 * モーダルコンポーネントです。
 * @param trigger {string} モーダルのトリガー要素（CSSセレクター）
 * @param cloneContent {string} モーダルに挿入するクローン対象のコンテンツ（CSSセレクター）
 * @param closeButton {string} モーダルを閉じる要素（CSSセレクター）
 * @param content {string} コンテンツを挿入する要素（ID）
 * @param modal {string} モーダルのルート要素（ID）
 * @param focus {string} モーダルを開くときにフォーカスする要素（ID）
 * @param overlay {string} モーダルの背景要素（ID）
 * @param openClass {string} モーダルを開くときに付与するクラス名
 * @param {boolean|function} beforeOpen [false] モーダルを開く前に実行するコールバック関数（イベント、コンテンツ、トリガー要素）。
 * @param {boolean|function} afterOpen [false] モーダルを開いたあとにに実行するコールバック関数（イベント、コンテンツ、トリガー要素）。
 * @param {boolean|function} beforeClose [false] モーダルを閉じる前に実行するコールバック関数（イベント、トリガー要素）。
 * @param {boolean|function} afterClose [false] モーダルを閉じたあとに実行するコールバック関数（イベント、トリガー要素）。
 * @example
 * const modal = new Modal({
 *   openClass: '-active',
 *   afterOpen(event, content, triggerElement) {
 *     console.log({ event, content, triggerElement });
 *   },
 *   afterClose(event, triggerElement) {
 *     console.log({ event, triggerElement });
 *   },
 * });
 * window.addEventListener('load', () => {
 *   modal.init();
 * });
 */
export default class Modal {
  constructor(options) {
    const defaultOptions = {
      trigger: selector.trigger,
      cloneContent: selector.cloneContent,
      closeButton: selector.closeButton,
      content: selector.contentID,
      modal: selector.modalID,
      focus: selector.focusID,
      overlay: selector.overlayID,
      openClass: '-active',
      beforeOpen: false,
      afterOpen: false,
      beforeClose: false,
      afterClose: false,
    };

    this.options = Object.assign(defaultOptions, options);

    Object.keys(this.options).forEach(key => {
      this[key] = this.options[key];
    });

    this.selector = {
      trigger: document.querySelectorAll(this.trigger),
      cloneContent: document.querySelectorAll(this.cloneContent),
      closeButton: document.querySelectorAll(this.closeButton),
      content: document.getElementById(this.content),
      modal: document.getElementById(this.modal),
      focus: document.getElementById(this.focus),
      overlay: document.getElementById(this.overlay),
    };
  }

  init() {
    if (!this.selector.trigger) return;

    this.setAttributes();
    this.open();
    this.clickClose();
    this.escapeClose();
    this.overlayClose();
    this.focusTrap();
  }

  setAttributes() {
    Array.from(this.selector.trigger).forEach((item, index) => {
      const datasetName = `data-${this.trigger.replace('.', '')}`;
      item.setAttribute(datasetName, index);
      item.setAttribute('id', `${this.trigger.replace('.', '')}`);
      item.setAttribute('aria-controls', `${this.cloneContent.replace('.', '')}`);
      item.setAttribute('aria-expanded', 'false');
    });
    Array.from(this.selector.cloneContent).forEach((item, index) => {
      const datasetName = `data-${this.cloneContent.replace('.', '')}`;
      item.setAttribute(datasetName, index);
    });
    this.selector.modal.setAttribute('id', `${this.cloneContent.replace('.', '')}`);
    this.selector.modal.setAttribute('aria-hidden', 'true');
    this.selector.modal.setAttribute('aria-labelledby', `${this.trigger.replace('.', '')}`);
  }

  open() {
    Array.from(this.selector.trigger).forEach(item => {
      item.addEventListener('click', event => {
        triggerElement = document.activeElement;
        scrollAmount = window.pageYOffset;
        const target = event.target.closest(`${this.trigger}`);
        const targetDataset = target.dataset[toCamelcase(this.trigger).replace('.', '')];
        const content = document.querySelector(
          `[data-${this.cloneContent.replace('.', '')}="${targetDataset}"]`,
        );

        if (this.beforeOpen !== false) {
          this.beforeOpen(event, content, triggerElement);
        }

        this.selector.content.textContent = '';
        this.selector.content.insertAdjacentElement('afterbegin', content.cloneNode(true));
        target.setAttribute('aria-expanded', 'true');
        fixed();
        this.selector.modal.classList.add(this.openClass);
        this.selector.modal.setAttribute('aria-hidden', 'false');
        setTimeout(() => {
          this.selector.focus.focus();
        }, 100);

        if (this.afterOpen !== false) {
          this.afterOpen(event, content, triggerElement);
        }
      });
    });
  }

  clickClose() {
    Array.from(this.selector.closeButton).forEach(item => {
      item.addEventListener('click', event => {
        if (this.beforeClose !== false) {
          this.beforeClose(event, triggerElement);
        }

        clearFixed();
        window.scrollTo(0, scrollAmount);
        this.selector.focus.scrollTop = 0;
        this.selector.modal.classList.remove(this.openClass);
        this.selector.trigger[0].setAttribute('aria-expanded', 'false');
        this.selector.modal.setAttribute('aria-hidden', 'true');
        triggerElement.focus();

        if (this.afterClose !== false) {
          this.afterClose(event, triggerElement);
        }
      });
    });
  }

  overlayClose() {
    this.selector.overlay.addEventListener('click', event => {
      if (this.beforeClose) {
        this.beforeClose(event, triggerElement);
      }

      clearFixed();
      window.scrollTo(0, scrollAmount);
      this.selector.focus.scrollTop = 0;
      this.selector.modal.classList.remove(this.openClass);
      this.selector.trigger[0].setAttribute('aria-expanded', 'false');
      this.selector.modal.setAttribute('aria-hidden', 'true');
      triggerElement.focus();

      if (this.afterClose) {
        this.afterClose(event, triggerElement);
      }
    });
  }

  escapeClose() {
    document.addEventListener('keyup', event => {
      const isEsc = !!(event.key === 'Escape' || event.key === 'Esc');
      if (isEsc) {
        if (this.beforeClose) {
          this.beforeClose(event, triggerElement);
        }

        clearFixed();
        window.scrollTo(0, scrollAmount);
        this.selector.focus.scrollTop = 0;
        this.selector.modal.classList.remove(this.openClass);
        this.selector.trigger[0].setAttribute('aria-expanded', 'false');
        this.selector.modal.setAttribute('aria-hidden', 'true');
        if (triggerElement) {
          triggerElement.focus();
        }

        if (this.afterClose) {
          this.afterClose(event, triggerElement);
        }
      }
    });
  }

  /**
   * モーダル要素内のフォーカス可能な要素で循環する。
   * @return {void}
   */
  focusTrap() {
    document.addEventListener('keydown', event => {
      const focusable = this.focusableElements();
      const isTab = !event.shiftKey && event.key === 'Tab';
      const isShiftTab = event.shiftKey && event.key === 'Tab';

      if (isTab && document.activeElement === focusable.last) {
        event.preventDefault();
        focusable.first.focus();
      }
      if (isShiftTab && document.activeElement === focusable.first) {
        event.preventDefault();
        focusable.last.focus();
      }
    });
  }

  /**
   * モーダル内のフォーカス可能な最初と最後、すべての要素を返す。
   * @return {Object} first, last, all - 最初の要素、最後の要素、すべての要素
   */
  focusableElements() {
    const elements = document.querySelectorAll(
      `#${this.modal} a, #${this.modal} button, #${this.modal} input, #${this.modal} textarea, #${this.modal} select, #${this.modal} details, #${this.modal} [tabindex]:not([tabindex="-1"])`,
    );
    return {
      first: elements[0],
      last: elements[elements.length - 1],
      all: elements,
    };
  }
}
